import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Dumbbell,
  Heart,
  Home,
  Microscope,
  Stethoscope,
} from 'lucide-react';
import {
  defaultServicesPageContent,
  mergeServicesPageContent,
} from '../content/defaultTenantSitePages';
import { useAuth } from '../context/AuthContext';
import { useClinicProfile } from '../context/ClinicProfileContext';
import { useTenantSlug } from '../context/TenantSlugContext';
import { TenantSitePagesService } from '../services/TenantSitePagesService';
import type {
  TenantServicesPageContent,
  TenantSiteFeatureItem,
} from '../types/site-pages';
import { buildTenantPath } from '../utils/tenantSlug';

const serviceIconMap = {
  stethoscope: Stethoscope,
  dumbbell: Dumbbell,
  heart: Heart,
  activity: Activity,
  home: Home,
  microscope: Microscope,
} as const;

const ServicesPage = () => {
  const { selectedClient } = useAuth();
  const { resolvedTenant } = useTenantSlug();
  const { effectiveTenantSlug } = useClinicProfile();
  const [content, setContent] = useState<TenantServicesPageContent>(() => defaultServicesPageContent);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadContent = async () => {
      const selectedClientId = selectedClient?.clientId ?? null;
      const currentTenantSlug = effectiveTenantSlug ?? null;

      if (!selectedClientId && !currentTenantSlug) {
        setContent(defaultServicesPageContent);
        setIsLoadingContent(false);
        return;
      }

      setIsLoadingContent(true);

      try {
        const pageRecord = selectedClientId
          ? await TenantSitePagesService.getByClientId(selectedClientId, 'services')
          : currentTenantSlug
            ? await TenantSitePagesService.getPublicBySlug(currentTenantSlug, 'services')
            : null;

        if (isCancelled) {
          return;
        }

        setContent(mergeServicesPageContent(pageRecord?.contentJson ?? null));
      } catch (error) {
        console.error('Erro ao carregar conteúdo institucional da página de serviços:', error);

        if (!isCancelled) {
          setContent(defaultServicesPageContent);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingContent(false);
        }
      }
    };

    void loadContent();

    return () => {
      isCancelled = true;
    };
  }, [effectiveTenantSlug, resolvedTenant?.clientId, selectedClient?.clientId]);

  const heroTitle =
    content.hero?.title ||
    defaultServicesPageContent.hero?.title ||
    'Nossos Serviços';
  const heroSubtitle =
    content.hero?.subtitle ||
    defaultServicesPageContent.hero?.subtitle ||
    '';
  const heroImageUrl =
    content.hero?.backgroundImage?.downloadUrl ||
    defaultServicesPageContent.hero?.backgroundImage?.downloadUrl ||
    '';
  const heroImageAlt =
    content.hero?.backgroundImage?.alt ||
    defaultServicesPageContent.hero?.backgroundImage?.alt ||
    'Imagem institucional da clínica';

  const introTitle =
    content.intro?.title ||
    defaultServicesPageContent.intro?.title ||
    '';
  const introBody =
    content.intro?.body ||
    defaultServicesPageContent.intro?.body ||
    '';

  const servicesTitle =
    content.services?.title ||
    defaultServicesPageContent.services?.title ||
    'Especialidades e atendimentos';
  const servicesSubtitle =
    content.services?.subtitle ||
    defaultServicesPageContent.services?.subtitle ||
    '';
  const serviceItems =
    content.services?.items ||
    defaultServicesPageContent.services?.items ||
    [];

  const ctaTitle =
    content.cta?.title ||
    defaultServicesPageContent.cta?.title ||
    'Precisa de atendimento especializado?';
  const ctaBody =
    content.cta?.body ||
    defaultServicesPageContent.cta?.body ||
    '';
  const ctaLabel =
    content.cta?.primaryCta?.label ||
    defaultServicesPageContent.cta?.primaryCta?.label ||
    'Agendar Consulta';
  const ctaPath =
    content.cta?.primaryCta?.path ||
    defaultServicesPageContent.cta?.primaryCta?.path ||
    '/agendar';

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="relative overflow-hidden bg-slate-950">
        {heroImageUrl ? (
          <div className="absolute inset-0">
            <img
              src={heroImageUrl}
              alt={heroImageAlt}
              className="h-full w-full object-cover object-center opacity-35"
            />
          </div>
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-900/80" />

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight text-white md:text-5xl">
              {heroTitle}
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">
              {heroSubtitle}
            </p>
          </div>

          {isLoadingContent ? (
            <div className="mt-8 inline-flex items-center gap-3 rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm text-slate-200">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Carregando conteúdo da página de serviços...
            </div>
          ) : null}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="max-w-3xl">
            <h2 className="text-2xl font-semibold text-slate-900">{introTitle}</h2>
            <p className="mt-4 text-base leading-7 text-slate-600">{introBody}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900">{servicesTitle}</h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-slate-600">
            {servicesSubtitle}
          </p>
        </div>

        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {serviceItems.map((service: TenantSiteFeatureItem, index) => {
            const IconComponent =
              serviceIconMap[
                (service.iconKey as keyof typeof serviceIconMap) || 'stethoscope'
              ] || Stethoscope;

            return (
              <div
                key={service.id || index}
                className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-md"
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-600 text-white">
                  <IconComponent className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-center text-xl font-semibold text-slate-900">
                  {service.title}
                </h3>
                <p className="mt-4 text-center leading-7 text-slate-600">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-primary-600 px-8 py-12 text-center shadow-lg">
          <h2 className="text-3xl font-bold text-white">{ctaTitle}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-primary-100">
            {ctaBody}
          </p>
          <Link
            to={buildTenantPath(ctaPath, effectiveTenantSlug)}
            className="mt-8 inline-flex items-center rounded-xl bg-white px-6 py-3 text-base font-semibold text-primary-700 transition hover:bg-primary-50"
          >
            {ctaLabel}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
